import React, {
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  toast,
  ToastOptions,
  Id
} from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';

import { useKeycloak } from '@react-keycloak/web';

import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';

import { useTranslation } from 'react-i18next';

type ContractResult = {
  poi: string;
  status: string;
  message: string;
  level: number;
  displayMessage: string;
};

const AGGREGATION_WINDOW_MS = 10000;

const SUMMARY_TOAST_ID = 'terminal-contract-summary';

const SignalRProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [connection, setConnection] =
    useState<HubConnection | null>(null);

  const isStartingRef = useRef(false);

  const resultsRef = useRef<ContractResult[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const toastIdRef = useRef<Id | null>(null);

  const { keycloak } = useKeycloak();
  const { t } = useTranslation();

  const toastConfig: ToastOptions = {
    position: 'top-center',
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: 'light'
  };

  useEffect(() => {
    console.log('[SignalR] Provider mounted');

    return () => {
      console.trace('[SignalR] Provider unmounted');
    };
  }, []);

  useEffect(() => {
    console.log('[SignalR] connection effect executed', {
      authenticated: keycloak.authenticated,
      connectionState: connection?.state
    });

    if (!keycloak.authenticated) {
      return;
    }

    if (connection || isStartingRef.current) {
      return;
    }

    let disposed = false;
    let newConnection: HubConnection | null = null;

    const renderResults = (results: ContractResult[]) => {
      const succeeded = results.filter(
        result => result.level < 3
      ).length;

      const failures = results.filter(
        result => result.level >= 3
      );

      if (failures.length === 0) {
        return (
          <div>
            {succeeded} operation(s) completed successfully.
          </div>
        );
      }

      return (
        <div>
          <div>
            {succeeded} succeeded and {failures.length} failed.
          </div>

          {failures.map((result, index) => (
            <span
              key={`${result.poi}-${index}`}
              style={{
                display: 'block',
                marginTop: '0.5rem'
              }}
            >
              • {result.displayMessage}
            </span>
          ))}
        </div>
      );
    };

    const updateResultsToast = (isFinal: boolean) => {
      const results = resultsRef.current;

      if (results.length === 0) {
        return;
      }

      const failures = results.filter(
        result => result.level >= 3
      );

      const errors = results.filter(
        result => result.level > 3
      );

      const toastType =
        failures.length === 0 ? 'success'
        : errors.length > 0 ? 'error' : 'warning';

      const content = renderResults(results);

      if (
        toastIdRef.current !== null &&
        toast.isActive(toastIdRef.current)
      ) {
        toast.update(toastIdRef.current, {
          ...toastConfig,
          render: content,
          type: toastType,
          autoClose: isFinal ? 30000 : false
        });
      } else {
        toastIdRef.current = toast(content, {
          ...toastConfig,
          toastId: SUMMARY_TOAST_ID,
          type: toastType,
          autoClose: isFinal ? 30000 : false
        });
      }
    };

    const flushResults = () => {
      if (resultsRef.current.length === 0) {
        timerRef.current = null;
        return;
      }

      // Make the current content final and enable auto-close.
      updateResultsToast(true);

      resultsRef.current = [];
      timerRef.current = null;
    };

    const handleUpdateCompleted = (
      poi: string,
      status: string,
      message?: string,
      level?: number
    ) => {
      const poiText = String(poi ?? '');
      const statusText = String(status ?? '');
      const messageText = String(message ?? '');
      const levelValue = level ?? 0;

      const success = messageText
        .toLowerCase()
        .includes('parameters updated');

      const displayMessage = success
        ? t('toast.success')
        : `${messageText} (POI: ${poiText})`;

      console.log('[SignalR] result', {
        poi: poiText,
        status: statusText,
        message: messageText,
        success,
        displayMessage
      });

      resultsRef.current.push({
        poi: poiText,
        status: statusText,
        message: messageText,
        level: levelValue,
        displayMessage
      });

      /*
       * Show the first result immediately.
       * For later results, update the existing toast.
       */
      updateResultsToast(false);

      /*
       * Start one fixed aggregation window from the first result.
       *
       * Do not reset this timer for every event. That would create
       * a debounce window and delay the toast until 10 seconds after
       * the last event.
       */
      if (!timerRef.current) {
        timerRef.current = setTimeout(
          flushResults,
          AGGREGATION_WINDOW_MS
        );
      }
    };

    const startConnection = async () => {
      isStartingRef.current = true;

      try {
        await keycloak
          .updateToken(-1)
          .catch(() =>
            console.error('[SignalR] Token refresh failed')
          );

        const hubUrl =
          `${process.env.REACT_APP_API_ENDPOINT}/hubs/terminalstatus`;

        newConnection = new HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => keycloak.token!
          })
          .configureLogging(LogLevel.Information)
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: retryContext => {
              if (retryContext.previousRetryCount <= 2) {
                return 1000;
              }

              if (retryContext.previousRetryCount <= 5) {
                return 5000;
              }

              return 10000;
            }
          })
          .build();

        // Register the handler before start().
        newConnection.on(
          'UpdateCompleted',
          handleUpdateCompleted
        );

        newConnection.onreconnecting(error => {
          console.warn(
            '[SignalR] reconnecting',
            error
          );
        });

        newConnection.onreconnected(connectionId => {
          console.info(
            '[SignalR] reconnected',
            connectionId
          );
        });

        newConnection.onclose(error => {
          console.error(
            '[SignalR] closed',
            error
          );

          setConnection(null);
        });

        await newConnection.start();

        if (disposed) {
          await newConnection.stop();
          return;
        }

        console.log('[SignalR] connected');

        setConnection(newConnection);
      } catch (err) {
        const error = err as Error;

        if (
          error.message?.includes('stop()') ||
          error.message?.includes('negotiation')
        ) {
          console.log(
            '[SignalR] Ignoring race condition during stop'
          );
        } else {
          console.error(
            '[SignalR] start failed:',
            error
          );
        }
      } finally {
        isStartingRef.current = false;
      }
    };

    void startConnection();

    return () => {
      console.trace(
        '[SignalR] connection effect cleanup'
      );

      console.warn('[SignalR] cleanup started', {
        connectionState: newConnection?.state
      });

      disposed = true;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      resultsRef.current = [];
      toastIdRef.current = null;

      if (newConnection) {
        newConnection.off(
          'UpdateCompleted',
          handleUpdateCompleted
        );

        if (
          newConnection.state !==
          HubConnectionState.Disconnected
        ) {
          console.warn(
            '[SignalR] stopping connection during cleanup'
          );

          void newConnection.stop();
        }
      }

      setConnection(current =>
        current === newConnection ? null : current
      );
    };
  }, [keycloak.authenticated]);

  return <>{children}</>;
};

export default SignalRProvider;